import styled from "@emotion/styled";
import { useState } from "react";
import { GallerySearchResults } from "./gallery-search-results/GallerySearchResults";
import { ChannelSearchResults } from "./channel-search-results/ChannelSearchResults";
import { EntrySearchResults } from "./entry-search-results/EntrySearchResults";
import { ResultsCount } from "./ResultsCount";
import { GlobalSearchResultsResponse } from "@kaltura/mediaspace-shared-types";
import { Pagination } from "@kaltura/ds-react-components";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { SearchResultsInnerComponentProps } from "@kaltura/mediaspace-shared-search";
import { translate } from "@kaltura/mediaspace-shared-utils";

export type SearchResultsProps = GlobalSearchResultsResponse & SearchResultsInnerComponentProps;

const StyledSearchResults = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "isWideLayout";
        },
    }
)<{ isWideLayout: boolean }>(({ isWideLayout, theme }) => ({
    [theme.breakpoints.down("lg")]: {
        marginTop: isWideLayout ? undefined : theme.spacing(3),
    },
    [theme.breakpoints.down("md")]: {
        marginTop: theme.spacing(3),
    },
}));

/**
 * Global Search Results
 */
export function SearchResults(props: SearchResultsProps) {
    const {
        channelResults,
        galleryResults,
        entryResults,
        page = 1,
        onPageChange,
        pageSize,
        onEntryShowMore,
        isWideLayout,
        isTextSearch,
        searchInLabel,
    } = props;
    const [activeEntry, setActiveEntry] = useState<string>();

    // first 3 entry results (above category results)
    const entries = entryResults?.objects.slice(0, 3);
    // rest of entry results (below category results)
    const restEntries = entryResults?.objects.slice(3, entryResults.objects.length);

    // show pager
    const showPager = !!entryResults && entryResults.totalCount > entryResults.objects.length;
    const pageCount = Math.ceil((entryResults?.totalCount ?? 0) / pageSize);

    // handle pager change
    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        onPageChange(value);
    };

    const handleMore = (searchResult: KalturaESearchEntryResult) => {
        setActiveEntry(searchResult.object.id);
        onEntryShowMore(searchResult);
    };

    return (
        <StyledSearchResults isWideLayout={isWideLayout} className="kms-ds-global-search-results">
            {entries && <ResultsCount totalCount={entryResults?.totalCount ?? 0} searchInLabel={searchInLabel} />}
            {entries && (
                <EntrySearchResults
                    title={translate("media results")}
                    isTextSearch={isTextSearch}
                    isWideLayout={isWideLayout}
                    entries={entries}
                    activeEntry={activeEntry}
                    onMore={handleMore}
                />
            )}
            {channelResults && <ChannelSearchResults {...channelResults} isWideLayout={isWideLayout} />}
            {galleryResults && <GallerySearchResults {...galleryResults} />}
            {restEntries && (
                <EntrySearchResults
                    title={translate("more media results")}
                    isTextSearch={isTextSearch}
                    isWideLayout={isWideLayout}
                    entries={restEntries}
                    activeEntry={activeEntry}
                    onMore={handleMore}
                />
            )}
            {showPager && (
                <div className="kms-ds-global-search-pagination">
                    {<Pagination count={pageCount} page={page} onChange={handleChange} />}
                </div>
            )}
        </StyledSearchResults>
    );
}

export default SearchResults;
